import { type Tooltip as TooltipType } from "primereact/tooltip";
import { type FC, type RefObject, useCallback, useEffect, useRef } from "react";

import { Tooltip } from "../tooltips";
import { resolveTruncatedCellTooltip } from "./truncated-cell";

type TruncatedCellTooltipProps = {
  wrapperRef: RefObject<HTMLDivElement>;
};

export const TruncatedCellTooltip: FC<TruncatedCellTooltipProps> = ({ wrapperRef }) => {
  const tooltipRef = useRef<TooltipType>(null);
  const activeElementRef = useRef<HTMLElement | null>(null);

  const hideTooltip = useCallback(() => {
    const active = activeElementRef.current;
    if (active?.hasAttribute("data-pr-tooltip")) {
      active.removeAttribute("data-pr-tooltip");
    }
    activeElementRef.current = null;
    tooltipRef.current?.hide();
  }, []);

  const showTooltip = useCallback(
    (event: MouseEvent) => {
      const resolved = resolveTruncatedCellTooltip(event.target);
      if (!resolved) {
        if (activeElementRef.current) {
          hideTooltip();
        }
        return;
      }

      // Same target: do not call show() again — applyDelay would restart showDelay.
      if (activeElementRef.current === resolved.element) {
        return;
      }

      // Switch targets without hide(): hide()+show() while visible races PrimeReact's
      // visibleState (show takes the update path, then hide commits visible=false).
      const previous = activeElementRef.current;
      if (previous?.hasAttribute("data-pr-tooltip")) {
        previous.removeAttribute("data-pr-tooltip");
      }

      resolved.element.setAttribute("data-pr-tooltip", resolved.text);
      activeElementRef.current = resolved.element;

      // PrimeReact's show() reads currentTarget/pageX/pageY from a native-like event.
      tooltipRef.current?.show({
        currentTarget: resolved.element,
        pageX: event.pageX,
        pageY: event.pageY,
      } as unknown as Parameters<TooltipType["show"]>[0]);
    },
    [hideTooltip],
  );

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) {
      return undefined;
    }

    const onMouseOver = (event: MouseEvent) => {
      if (!(event.target instanceof Element) || !event.target.closest("td, th")) {
        return;
      }

      showTooltip(event);
    };

    const onMouseOut = (event: MouseEvent) => {
      const active = activeElementRef.current;
      if (!active) {
        return;
      }

      const related = event.relatedTarget;
      if (related instanceof Node && active.contains(related)) {
        return;
      }

      const fromCell = active.closest("td, th");
      if (related instanceof Node && fromCell?.contains(related)) {
        // Still inside the same cell — mouseover will re-resolve if the target changed.
        return;
      }

      // Moving into another cell: let mouseover switch targets without hide().
      if (related instanceof Element && related.closest("td, th") && wrapper.contains(related)) {
        return;
      }

      hideTooltip();
    };

    wrapper.addEventListener("mouseover", onMouseOver);
    wrapper.addEventListener("mouseout", onMouseOut);

    return () => {
      wrapper.removeEventListener("mouseover", onMouseOver);
      wrapper.removeEventListener("mouseout", onMouseOut);
      hideTooltip();
    };
  }, [wrapperRef, showTooltip, hideTooltip]);

  return <Tooltip ref={tooltipRef} position="top" showDelay={300} />;
};
